import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Link, useNavigate } from 'react-router';
import {
  CameraIcon,
  CompassIcon,
  ArrowLeftIcon,
  Loader2Icon,
  AlertCircleIcon,
  MapPinIcon,
  ChevronRightIcon,
  CrosshairIcon,
  ZapIcon,
  ListIcon,
  XIcon,
  RefreshCwIcon,
  ScanLineIcon,
  TargetIcon,
  CheckCircle2Icon,
  TrophyIcon,
  UploadIcon,
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import {
  getGetNearbyAssetsQueryOptions,
  type NearbyAsset,
  type PublicChallenge,
} from '@cdf/api-client-react';
import { useCamera, type CameraState } from '@/hooks/use-camera';
import { useGeolocation } from '@/hooks/use-geolocation';
import { useHeading } from '@/hooks/use-heading';
import { useHuntChallenges } from '@/hooks/use-hunt-challenges';
import { useCvScanner, type CvScanState, type CvMatchResult } from '@/hooks/use-cv-scanner';
import { submitCvCapture, useCvTargetsActive, type ActiveCvTarget } from '@/lib/cv-api';
import { projectAsset, bearingLabel, AR_MAX_DISTANCE } from '@/lib/ar-projection';
import { formatDistance, getStatusConfig } from '@/lib/report-helpers';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { FireworksStreamersOverlay } from '@/components/ar/fireworks-streamers-overlay';
import { cn } from '@/lib/utils';

type ArMode = 'onboarding' | 'pseudo-ar' | 'list-fallback' | 'webxr' | 'cv-scanner';

export function ArOverlayPage() {
  const navigate = useNavigate();
  const [mode, setMode] = useState<ArMode>('onboarding');
  const [webxrSupported, setWebxrSupported] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState<NearbyAsset | null>(null);

  const camera = useCamera();
  const geo = useGeolocation();
  const heading = useHeading();

  // Hunt challenge: fire ar_view completion when overlay reaches active state
  const { session, incompleteByType, incompleteChallenges, completeChallenge: completeHuntChallenge } =
    useHuntChallenges();
  const arChallenge = incompleteByType.ar_view;
  useEffect(() => {
    if (arChallenge && mode === 'pseudo-ar' && camera.state === 'active') {
      completeHuntChallenge(arChallenge.id);
    }
  }, [arChallenge, mode, camera.state, completeHuntChallenge]);

  // ── CV Scanner ────────────────────────────────────────────────────────
  const videoElementRef = useRef<HTMLVideoElement | null>(null);
  const { data: cvTargetsData } = useCvTargetsActive(session?.huntSlug);
  const cvTargets = cvTargetsData?.targets ?? [];
  const cvScan = useCvScanner(videoElementRef, cvTargets);

  // Combined ref: local scanner ref + camera stream attach.
  // Depend on the stable callback from useCamera, not the whole camera object
  // (camera identity changes with state and would detach <video> every update).
  const cameraVideoRef = camera.videoRef;
  const cameraStart = camera.start;
  const videoRefCallback = useCallback((node: HTMLVideoElement | null) => {
    videoElementRef.current = node;
    cameraVideoRef(node);
  }, [cameraVideoRef]);

  // The active target determines its exact CV challenge; do not complete a generic
  // challenge client-side before the server has validated the target binding.
  const cvScanChallenge = incompleteChallenges.find(
    (challenge) => (challenge.type as string) === 'cv_scan'
  );
  const completedTargetRef = useRef<Set<string>>(new Set());
  const autoStartedCvScannerRef = useRef(false);
  const [verifiedCapture, setVerifiedCapture] = useState<{ targetId: string; pointsAwarded: number } | null>(null);
  const [cvCaptureError, setCvCaptureError] = useState<string | null>(null);

  const submitVerifiedMatch = useCallback(async (
    match: NonNullable<typeof cvScan.bestMatch>,
    source: 'camera' | 'upload',
    file?: File,
  ) => {
    const target = cvTargets.find((candidate) => candidate.id === match.targetId);
    if (!target?.huntChallengeId || !session) return;
    if (completedTargetRef.current.has(target.id)) return;

    completedTargetRef.current.add(target.id);
    setCvCaptureError(null);
    try {
      const image = file && (file.type === 'image/jpeg' || file.type === 'image/png' || file.type === 'image/webp')
        ? { filename: file.name, mimeType: file.type as 'image/jpeg' | 'image/png' | 'image/webp', sizeBytes: file.size }
        : undefined;
      const result = await submitCvCapture(session.huntSlug, session.token, {
        targetId: target.id,
        challengeId: target.huntChallengeId,
        matchScore: match.score,
        source,
        image,
      });
      setVerifiedCapture({ targetId: result.targetId, pointsAwarded: result.pointsAwarded });
    } catch (error) {
      completedTargetRef.current.delete(target.id);
      setCvCaptureError(error instanceof Error ? error.message : 'Unable to verify this scan. Please try again.');
    }
  }, [cvTargets, session, cvScan.bestMatch]);

  const [pendingMatch, setPendingMatch] = useState<CvMatchResult | null>(null);
  const [isRecording, setIsRecording] = useState(false);

  // When a match is detected, show the celebration overlay with a Record button
  // instead of auto-submitting — the user explicitly confirms the capture.
  useEffect(() => {
    if (mode === 'cv-scanner' && cvScan.bestMatch && !verifiedCapture && !isRecording) {
      setPendingMatch(cvScan.bestMatch);
    }
  }, [mode, cvScan.bestMatch, verifiedCapture, isRecording]);

  const handleRecord = useCallback(async () => {
    if (!pendingMatch) return;
    setIsRecording(true);
    await submitVerifiedMatch(pendingMatch, 'camera');
    setIsRecording(false);
    setPendingMatch(null);
  }, [pendingMatch, submitVerifiedMatch]);

  const handleSkipMatch = useCallback(() => {
    setPendingMatch(null);
  }, []);

  const handleDismissVerified = useCallback(() => {
    setVerifiedCapture(null);
    setPendingMatch(null);
  }, []);

  const handlePhotoSelected = useCallback(async (file: File) => {
    setCvCaptureError(null);
    try {
      const match = await cvScan.scanImageFile(file);
      if (!match) {
        setCvCaptureError('No conference hunt target was recognised in that photo.');
        return;
      }
      await submitVerifiedMatch(match, 'upload', file);
    } catch (error) {
      setCvCaptureError(error instanceof Error ? error.message : 'Unable to scan that photo.');
    }
  }, [cvScan, submitVerifiedMatch]);

  // Mount the scanner view first so <video> exists, then start the camera.
  const handleStartCvScanner = useCallback(async () => {
    setMode('cv-scanner');
    try {
      await cameraStart();
    } catch {
      // Photo upload remains available when the device camera is unavailable or denied.
    }
  }, [cameraStart]);

  // Auto-open CV scanner once when a hunt session with image targets is active.
  useEffect(() => {
    if (mode !== 'onboarding' || !session?.huntSlug || cvTargets.length === 0) {
      if (mode === 'onboarding') autoStartedCvScannerRef.current = false;
      return;
    }
    if (autoStartedCvScannerRef.current) return;
    autoStartedCvScannerRef.current = true;
    void handleStartCvScanner();
  }, [mode, session?.huntSlug, cvTargets.length, handleStartCvScanner]);

  // Check WebXR immersive-ar support on mount
  useEffect(() => {
    const nav = navigator as unknown as { xr?: { isSessionSupported: (mode: string) => Promise<boolean> } };
    if (nav.xr?.isSessionSupported) {
      nav.xr.isSessionSupported('immersive-ar').then((supported) => {
        setWebxrSupported(supported);
      }).catch(() => setWebxrSupported(false));
    }
  }, []);

  // Fetch nearby assets when we have a position
  const { data: nearbyData, isLoading: nearbyLoading } = useQuery({
    ...getGetNearbyAssetsQueryOptions(
      { lat: geo.position?.lat ?? 0, lng: geo.position?.lng ?? 0, radius: AR_MAX_DISTANCE }
    ),
    enabled: mode !== 'onboarding' && !!geo.position,
    refetchInterval: 15000,
  });

  const assets = useMemo(() => nearbyData?.assets ?? [], [nearbyData]);

  // Start sensors
  const handleStart = useCallback(async () => {
    await Promise.all([cameraStart(), geo.start(), heading.start()]);
    // If camera is unsupported or denied, we'll fall back to list
    // The mode switch happens in the effect below
    setMode('pseudo-ar');
  }, [cameraStart, geo, heading]);

  // Pseudo-AR needs a live camera; fall back to list if unavailable.
  // CV scanner keeps its own UI (photo upload still works without a camera).
  useEffect(() => {
    if (
      mode === 'pseudo-ar' &&
      (camera.state === 'denied' || camera.state === 'unsupported' || camera.state === 'error')
    ) {
      setMode('list-fallback');
    }
  }, [mode, camera.state]);

  const handleStartList = useCallback(async () => {
    await geo.start();
    setMode('list-fallback');
  }, [geo]);

  const handleStartWebXR = useCallback(() => {
    setMode('webxr');
  }, []);

  const handleReportHere = useCallback((asset: NearbyAsset) => {
    const params = new URLSearchParams({
      lat: asset.latitude.toString(),
      lng: asset.longitude.toString(),
      assetId: asset.id,
    });
    navigate(`/report?${params.toString()}`);
  }, [navigate]);

  // ── Onboarding screen ──────────────────────────────────────────────
  if (mode === 'onboarding') {
    return (
      <div className="fixed inset-0 z-50 flex flex-col bg-background">
        <div className="flex items-center gap-2 border-b p-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeftIcon className="h-5 w-5" />
          </Button>
          <h1 className="text-lg font-semibold">AR Asset View</h1>
        </div>

        <div className="flex-1 overflow-y-auto">
          <div className="min-h-full flex flex-col items-center justify-center gap-6 p-6">
          <div className="flex h-20 w-20 items-center justify-center rounded-full bg-primary/10">
            <CameraIcon className="h-10 w-10 text-primary" />
          </div>

          <div className="max-w-md space-y-3 text-center">
            <h2 className="text-xl font-semibold">Point your phone at your surroundings</h2>
            <p className="text-sm text-muted-foreground">
              See overlaid labels for nearby infrastructure assets — what they are, who owns them,
              and any open issues with their remediation stage.
            </p>
          </div>

          <div className="flex w-full max-w-md flex-col gap-2">
            <Button size="lg" onClick={handleStart}>
              <CameraIcon className="mr-2 h-5 w-5" />
              Start AR View
            </Button>
            <div className="flex gap-2">
              {webxrSupported && (
                <Button variant="outline" className="flex-1" onClick={handleStartWebXR}>
                  <ZapIcon className="mr-2 h-4 w-4" />
                  WebXR Mode
                </Button>
              )}
              <Button variant="outline" className="flex-1" onClick={handleStartList}>
                <ListIcon className="mr-2 h-4 w-4" />
                List Only
              </Button>
            </div>
            <Button variant="outline" size="lg" onClick={handleStartCvScanner}>
              <ScanLineIcon className="mr-2 h-5 w-5" />
              CV Target Scanner
            </Button>
            <p className="text-center text-xs text-muted-foreground">
              No camera? The list view shows the same information without sensors.
            </p>
          </div>

          <div className="w-full max-w-md space-y-3">
            <div className="flex items-start gap-3 rounded-lg border p-3">
              <CameraIcon className="mt-0.5 h-5 w-5 shrink-0 text-muted-foreground" />
              <div className="text-sm">
                <p className="font-medium">Camera access</p>
                <p className="text-muted-foreground">Used to show the live camera feed behind asset labels.</p>
              </div>
            </div>
            <div className="flex items-start gap-3 rounded-lg border p-3">
              <MapPinIcon className="mt-0.5 h-5 w-5 shrink-0 text-muted-foreground" />
              <div className="text-sm">
                <p className="font-medium">Location access</p>
                <p className="text-muted-foreground">Finds nearby assets within 100m of your position. Image scanner works without GPS.</p>
              </div>
            </div>
            <div className="flex items-start gap-3 rounded-lg border p-3">
              <CompassIcon className="mt-0.5 h-5 w-5 shrink-0 text-muted-foreground" />
              <div className="text-sm">
                <p className="font-medium">Motion & compass</p>
                <p className="text-muted-foreground">Aligns overlay cards with the direction you're facing.</p>
              </div>
            </div>
          </div>
          </div>
        </div>
      </div>
    );
  }

  // ── WebXR mode placeholder ─────────────────────────────────────────
  if (mode === 'webxr') {
    return (
      <div className="fixed inset-0 z-50 flex flex-col bg-background">
        <div className="flex items-center gap-2 border-b p-4">
          <Button variant="ghost" size="icon" onClick={() => setMode('onboarding')}>
            <ArrowLeftIcon className="h-5 w-5" />
          </Button>
          <h1 className="text-lg font-semibold">WebXR AR Mode</h1>
        </div>
        <div className="flex-1 overflow-y-auto">
          <div className="min-h-full flex flex-col items-center justify-center gap-4 p-6 text-center">
            <ZapIcon className="h-12 w-12 text-primary" />
            <p className="max-w-sm text-sm text-muted-foreground">
              WebXR immersive-ar is supported on this device. The enhanced anchored mode renders
              asset labels as AR sprites positioned in real-world space.
            </p>
            <p className="text-xs text-muted-foreground">
              Using the pseudo-AR camera overlay for this demo. Full WebXR anchoring requires
              a WebXR session manager.
            </p>
            <Button onClick={() => { handleStart(); }}>
              Continue with Camera AR
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // ── List fallback view ─────────────────────────────────────────────
  if (mode === 'list-fallback' || ((mode === 'pseudo-ar' || mode === 'cv-scanner') && camera.state !== 'active')) {
    return (
      <div className="fixed inset-0 z-50 flex flex-col bg-background">
        <div className="flex items-center gap-2 border-b p-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeftIcon className="h-5 w-5" />
          </Button>
          <h1 className="text-lg font-semibold">Nearby Assets</h1>
          {geo.state === 'active' && (
            <Badge variant="secondary" className="ml-auto">
              <CrosshairIcon className="mr-1 h-3 w-3" />
              GPS Active
            </Badge>
          )}
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {geo.state === 'requesting' && (
            <div className="flex flex-col items-center gap-2 py-12 text-muted-foreground">
              <Loader2Icon className="h-6 w-6 animate-spin" />
              <p className="text-sm">Getting your location…</p>
            </div>
          )}

          {geo.state === 'denied' && (
            <div className="flex flex-col items-center gap-3 py-12 text-center">
              <AlertCircleIcon className="h-10 w-10 text-amber-500" />
              <p className="text-sm font-medium">Location permission needed</p>
              <p className="max-w-xs text-xs text-muted-foreground">
                Allow location access to see nearby assets. You can still browse the map instead.
              </p>
              <Button asChild variant="outline" size="sm">
                <Link to="/map">Open Map</Link>
              </Button>
            </div>
          )}

          {geo.state === 'active' && nearbyLoading && (
            <div className="flex flex-col items-center gap-2 py-12 text-muted-foreground">
              <Loader2Icon className="h-6 w-6 animate-spin" />
              <p className="text-sm">Finding nearby assets…</p>
            </div>
          )}

          {geo.state === 'active' && !nearbyLoading && assets.length === 0 && (
            <div className="flex flex-col items-center gap-3 py-12 text-center">
              <MapPinIcon className="h-10 w-10 text-muted-foreground" />
              <p className="text-sm font-medium">No civic assets nearby</p>
              <p className="max-w-xs text-xs text-muted-foreground">
                {session?.huntSlug ? 'Use the Image Scanner to find and scan hunt targets.' : 'Move closer to a managed area or explore the map to find assets.'}
              </p>
              <Button asChild variant="outline" size="sm">
                <Link to="/map">Open Map</Link>
              </Button>
            </div>
          )}

          {geo.state === 'active' && assets.length > 0 && (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">
                {assets.length} asset{assets.length !== 1 && 's'} nearby
              </p>
              {assets.map((asset) => (
                <AssetListItem
                  key={asset.id}
                  asset={asset}
                  onSelect={() => setSelectedAsset(asset)}
                />
              ))}
            </div>
          )}
        </div>

        {selectedAsset && (
          <AssetDetailSheet
            asset={selectedAsset}
            onClose={() => setSelectedAsset(null)}
            onReportHere={() => handleReportHere(selectedAsset)}
          />
        )}
      </div>
    );
  }

  // ── CV Scanner mode ──────────────────────────────────────────────────
  if (mode === 'cv-scanner') {
    return (
      <CvScannerView
        videoRef={videoRefCallback}
        cameraState={camera.state}
        cameraError={camera.error}
        scanState={cvScan}
        targets={cvTargets}
        cvScanChallenge={cvScanChallenge ?? null}
        verifiedCapture={verifiedCapture}
        pendingMatch={pendingMatch}
        isRecording={isRecording}
        captureError={cvCaptureError}
        onPhotoSelected={handlePhotoSelected}
        onRecord={handleRecord}
        onSkipMatch={handleSkipMatch}
        onDismissVerified={handleDismissVerified}
        onRetryCamera={() => {
          void camera.start();
        }}
        onBack={() => setMode('onboarding')}
      />
    );
  }

  // ── Pseudo-AR camera overlay ──────────────────────────────────────
  const userHeading = heading.heading ?? 0;

  return (
    <div className="fixed inset-0 z-50 overflow-hidden bg-black">
      {/* Camera feed */}
      <video
        ref={camera.videoRef}
        className="absolute inset-0 h-full w-full object-cover"
        autoPlay
        playsInline
        muted
      />

      {/* Dark gradient for readability */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-transparent to-black/40" />

      {/* Top bar */}
      <div className="absolute left-0 right-0 top-0 z-20 flex items-center gap-2 bg-black/50 p-3 backdrop-blur-sm">
        <Button variant="ghost" size="icon" className="text-white" onClick={() => navigate(-1)}>
          <ArrowLeftIcon className="h-5 w-5" />
        </Button>
        <div className="flex items-center gap-2 text-white">
          <CompassIcon className="h-4 w-4" />
          <span className="text-sm font-medium">
            {heading.heading !== null ? `${Math.round(heading.heading)}° ${bearingLabel(heading.heading)}` : 'No compass'}
          </span>
        </div>
        {geo.position && (
          <Badge variant="secondary" className="ml-auto bg-white/20 text-white">
            <CrosshairIcon className="mr-1 h-3 w-3" />
            {formatDistance(0).replace(' away', '')} GPS
          </Badge>
        )}
      </div>

      {/* Heading permission/unsupported banner */}
      {(heading.state === 'denied' || heading.state === 'unsupported') && (
        <div className="absolute left-0 right-0 top-14 z-20 flex items-center gap-2 bg-amber-500/90 p-2 text-black">
          <AlertCircleIcon className="h-4 w-4 shrink-0" />
          <span className="text-xs">
            {heading.state === 'denied'
              ? 'Motion permission denied. Enable Motion & Orientation in browser settings for compass alignment.'
              : 'Compass not available on this device. Asset positions are approximate.'}
          </span>
          <Button
            variant="ghost"
            size="sm"
            className="ml-auto h-7 text-xs text-black hover:bg-black/20"
            onClick={() => navigate('/map')}
          >
            Use Map
          </Button>
        </div>
      )}

      {/* Compass strip at top */}
      <CompassStrip
        assets={assets}
        userHeading={userHeading}
      />

      {/* Asset overlay cards */}
      <div className="absolute inset-0 z-10">
        {assets.map((asset) => {
          const projected = projectAsset(
            asset.bearing,
            userHeading,
            asset.distance
          );

          if (!projected.inView) return null;

          return (
            <div
              key={asset.id}
              className="absolute"
              style={{
                left: `${(projected.screenX ?? 0.5) * 100}%`,
                top: `${projected.screenY * 100}%`,
                transform: 'translate(-50%, -50%)',
              }}
              onClick={() => setSelectedAsset(asset)}
            >
              <AssetOverlayCard asset={asset} />
            </div>
          );
        })}
      </div>

      {/* Bottom info bar */}
      <div className="absolute bottom-0 left-0 right-0 z-20 bg-black/50 p-4 pb-[calc(1rem+env(safe-area-inset-bottom))] backdrop-blur-sm">
        {(geo.state === 'requesting' || geo.state === 'idle') && (
          <div className="flex items-center justify-center gap-2 text-white">
            <Loader2Icon className="h-4 w-4 animate-spin" />
            <span className="text-sm">Waiting for GPS…</span>
          </div>
        )}

        {geo.state === 'denied' && (
          <div className="flex flex-col items-center gap-2 text-center">
            <div className="flex items-center gap-2 text-white">
              <MapPinIcon className="h-4 w-4 text-amber-400" />
              <span className="text-sm font-medium">Location permission needed</span>
            </div>
            <p className="max-w-xs text-xs text-white/70">
              Enable location access to see nearby assets overlaid on the camera view.
            </p>
            <Button asChild variant="secondary" size="sm">
              <Link to="/map">Open Map Instead</Link>
            </Button>
          </div>
        )}

        {(geo.state === 'error' || geo.state === 'unsupported') && (
          <div className="flex flex-col items-center gap-2 text-center">
            <div className="flex items-center gap-2 text-white">
              <AlertCircleIcon className="h-4 w-4 text-amber-400" />
              <span className="text-sm font-medium">Location unavailable</span>
            </div>
            <p className="max-w-xs text-xs text-white/70">
              {geo.error ?? 'Could not determine your location.'}
            </p>
            <div className="flex gap-2">
              <Button variant="secondary" size="sm" onClick={() => geo.start()}>
                <RefreshCwIcon className="mr-1 h-3 w-3" />
                Retry GPS
              </Button>
              <Button asChild variant="outline" size="sm">
                <Link to="/map">Map</Link>
              </Button>
            </div>
          </div>
        )}

        {geo.state === 'active' && nearbyLoading && (
          <div className="flex items-center justify-center gap-2 text-white">
            <Loader2Icon className="h-4 w-4 animate-spin" />
            <span className="text-sm">Finding nearby assets…</span>
          </div>
        )}

        {geo.state === 'active' && !nearbyLoading && assets.length === 0 && (
          <p className="text-center text-sm text-white/70">
            No civic assets nearby. Use the Image Scanner to find hunt targets.
          </p>
        )}

        {geo.state === 'active' && !nearbyLoading && assets.length > 0 && (
          <p className="text-center text-sm text-white/70">
            {assets.length} asset{assets.length !== 1 && 's'} nearby · tap a card for details
          </p>
        )}
      </div>

      {/* Detail sheet */}
      {selectedAsset && (
        <AssetDetailSheet
          asset={selectedAsset}
          onClose={() => setSelectedAsset(null)}
          onReportHere={() => handleReportHere(selectedAsset)}
        />
      )}
    </div>
  );
}

// ── Asset overlay card (in pseudo-AR view) ──────────────────────────

function AssetOverlayCard({ asset }: { asset: NearbyAsset }) {
  const hasIssues = asset.openIssues.length > 0;
  return (
    <div
      className={cn(
        'flex cursor-pointer flex-col items-center gap-1 rounded-lg border-2 bg-black/60 px-3 py-2 backdrop-blur-sm transition-colors',
        hasIssues ? 'border-amber-400' : 'border-white/30'
      )}
    >
      <span className="text-2xl">{asset.assetTypeIcon}</span>
      <span className="text-xs font-medium text-white">{asset.assetTypeName}</span>
      <span className="text-[10px] text-white/60">{formatDistance(asset.distance)}</span>
      {hasIssues && (
        <span className="rounded-full bg-amber-500 px-1.5 py-0.5 text-[10px] font-semibold text-black">
          {asset.openIssues.length} open
        </span>
      )}
    </div>
  );
}

// ── Compass strip (off-screen asset indicators) ──────────────────────

function CompassStrip({ assets, userHeading }: { assets: NearbyAsset[]; userHeading: number }) {
  const offscreen = assets
    .map((a) => projectAsset(a.bearing, userHeading, a.distance))
    .filter((p) => !p.inView)
    .slice(0, 8);

  if (offscreen.length === 0) return null;

  return (
    <div className="absolute left-0 right-0 top-14 z-15 flex h-8 items-center justify-center gap-1 overflow-hidden">
      {offscreen.map((p, i) => (
        <div
          key={i}
          className="flex h-6 w-6 items-center justify-center rounded-full bg-white/20 text-white backdrop-blur-sm"
          style={{
            marginLeft: p.offscreenDirection === -1 ? '0' : undefined,
            marginRight: p.offscreenDirection === 1 ? '0' : undefined,
          }}
        >
          <ChevronRightIcon
            className={cn('h-3 w-3', p.offscreenDirection === -1 && 'rotate-180')}
          />
        </div>
      ))}
    </div>
  );
}

// ── Asset list item (fallback view) ──────────────────────────────────

function AssetListItem({ asset, onSelect }: { asset: NearbyAsset; onSelect: () => void }) {
  const hasIssues = asset.openIssues.length > 0;
  const firstIssue = asset.openIssues[0];
  const statusCfg = firstIssue ? getStatusConfig(firstIssue.status) : null;

  return (
    <Card className={cn('cursor-pointer transition-colors hover:bg-muted/50', hasIssues && 'border-amber-300')}>
      <CardContent className="flex items-center gap-3 py-3" onClick={onSelect}>
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-muted text-xl">
          {asset.assetTypeIcon}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p className="truncate text-sm font-medium">{asset.assetTypeName}</p>
            {asset.condition && (
              <Badge variant="outline" className="shrink-0 text-xs">{asset.condition}</Badge>
            )}
          </div>
          <p className="text-xs text-muted-foreground">
            {formatDistance(asset.distance)} · {asset.tenantName}
          </p>
          {hasIssues && (
            <div className="mt-1 flex items-center gap-1.5">
              <span className={cn('h-2 w-2 rounded-full', statusCfg?.dotClass)} />
              <span className="text-xs text-muted-foreground">
                {asset.openIssues.length} open issue{asset.openIssues.length !== 1 && 's'}
                {firstIssue?.categoryName && ` · ${firstIssue.categoryName}`}
              </span>
            </div>
          )}
        </div>
        <ChevronRightIcon className="h-4 w-4 shrink-0 text-muted-foreground" />
      </CardContent>
    </Card>
  );
}

// ── Asset detail bottom sheet ───────────────────────────────────────

function AssetDetailSheet({
  asset,
  onClose,
  onReportHere,
}: {
  asset: NearbyAsset;
  onClose: () => void;
  onReportHere: () => void;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/50" onClick={onClose}>
      <div
        className="max-h-[70vh] w-full max-w-lg overflow-y-auto rounded-t-2xl bg-background p-5 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-muted text-2xl">
              {asset.assetTypeIcon}
            </div>
            <div>
              <h3 className="font-semibold">{asset.assetTypeName}</h3>
              <p className="text-sm text-muted-foreground">{formatDistance(asset.distance)}</p>
            </div>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <XIcon className="h-5 w-5" />
          </Button>
        </div>

        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <p className="text-muted-foreground">Owner</p>
              <p className="font-medium">{asset.tenantName}</p>
              <Badge variant="outline" className="mt-0.5 text-xs">
                {asset.tenantType === 'council' ? 'Council' : 'Service Provider'}
              </Badge>
            </div>
            <div>
              <p className="text-muted-foreground">Condition</p>
              <p className="font-medium">{asset.condition ?? 'Unknown'}</p>
            </div>
            {asset.address && (
              <div className="col-span-2">
                <p className="text-muted-foreground">Address</p>
                <p className="text-sm">{asset.address}</p>
              </div>
            )}
          </div>

          {asset.openIssues.length > 0 && (
            <div className="space-y-2">
              <p className="text-sm font-medium">Open Issues ({asset.openIssues.length})</p>
              {asset.openIssues.map((issue) => {
                const cfg = getStatusConfig(issue.status);
                return (
                  <div key={issue.id} className="flex items-center gap-2 rounded-lg border p-2">
                    <span className={cn('h-2 w-2 shrink-0 rounded-full', cfg.dotClass)} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium">{issue.categoryName ?? 'Unknown issue'}</p>
                      <p className="text-xs text-muted-foreground">
                        {cfg.label}
                        {issue.severity && ` · ${issue.severity}`}
                      </p>
                    </div>
                    <Button asChild size="sm" variant="ghost">
                      <Link to={`/track/${issue.trackingToken}`}>Track</Link>
                    </Button>
                  </div>
                );
              })}
            </div>
          )}

          {asset.openIssues.length === 0 && (
            <p className="text-sm text-muted-foreground">No open issues reported for this asset.</p>
          )}

          <Button className="w-full" onClick={onReportHere}>
            <CameraIcon className="mr-2 h-4 w-4" />
            Report Here
          </Button>
        </div>
      </div>
    </div>
  );
}

// ── CV Scanner view ──────────────────────────────────────────────────

function CvScannerView({
  videoRef,
  cameraState,
  cameraError,
  scanState,
  targets,
  cvScanChallenge,
  verifiedCapture,
  pendingMatch,
  isRecording,
  captureError,
  onPhotoSelected,
  onRecord,
  onSkipMatch,
  onDismissVerified,
  onRetryCamera,
  onBack,
}: {
  videoRef: (node: HTMLVideoElement | null) => void;
  cameraState: CameraState;
  cameraError: string | null;
  scanState: CvScanState;
  targets: ActiveCvTarget[];
  cvScanChallenge: PublicChallenge | null;
  verifiedCapture: { targetId: string; pointsAwarded: number } | null;
  pendingMatch: CvMatchResult | null;
  isRecording: boolean;
  captureError: string | null;
  onPhotoSelected: (file: File) => void;
  onRecord: () => void;
  onSkipMatch: () => void;
  onDismissVerified: () => void;
  onRetryCamera: () => void;
  onBack: () => void;
}) {
  const [showTargets, setShowTargets] = useState(false);
  const { bestMatch, allResults, boundingBox, scanProgress } = scanState;
  const verifiedTarget = verifiedCapture
    ? targets.find((target) => target.id === verifiedCapture.targetId) ?? null
    : null;
  const pendingTarget = pendingMatch
    ? targets.find((t) => t.id === pendingMatch.targetId) ?? null
    : null;
  const celebrateTarget = pendingTarget ?? verifiedTarget;
  const fireworksTargetId = celebrateTarget?.celebrationEffect === 'fireworks_streamers'
    ? celebrateTarget.id
    : null;
  const lastCelebratedTargetId = useRef<string | null>(null);
  const celebrationResetTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [celebrationTrigger, setCelebrationTrigger] = useState(0);
  const cameraReady = cameraState === 'active';
  const cameraBusy = cameraState === 'idle' || cameraState === 'requesting';

  useEffect(() => {
    if (fireworksTargetId) {
      if (celebrationResetTimer.current !== null) {
        clearTimeout(celebrationResetTimer.current);
        celebrationResetTimer.current = null;
      }
      if (lastCelebratedTargetId.current !== fireworksTargetId) {
        lastCelebratedTargetId.current = fireworksTargetId;
        setCelebrationTrigger((current) => current + 1);
      }
      return;
    }

    if (lastCelebratedTargetId.current !== null) {
      celebrationResetTimer.current = setTimeout(() => {
        lastCelebratedTargetId.current = null;
        celebrationResetTimer.current = null;
      }, 1_200);
    }

    return () => {
      if (celebrationResetTimer.current !== null) {
        clearTimeout(celebrationResetTimer.current);
        celebrationResetTimer.current = null;
      }
    };
  }, [fireworksTargetId]);

  // Clear "just matched" flash after 3 seconds
  const [justMatched, setJustMatched] = useState(false);
  useEffect(() => {
    if (bestMatch) {
      setJustMatched(true);
      const t = setTimeout(() => setJustMatched(false), 3000);
      return () => clearTimeout(t);
    }
  }, [bestMatch?.targetId]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="fixed inset-0 z-50 overflow-hidden bg-black">
      {/* Camera feed */}
      <video
        ref={videoRef}
        className="absolute inset-0 h-full w-full object-cover"
        autoPlay
        playsInline
        muted
      />

      {/* Dark gradient for readability */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-transparent to-black/40" />

      {/* Camera status while the live feed is starting or unavailable */}
      {!cameraReady && (
        <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 bg-black/70 p-6 text-center">
          {cameraBusy ? (
            <>
              <CameraIcon className="h-10 w-10 animate-pulse text-white/80" />
              <p className="text-sm font-medium text-white">Starting camera…</p>
              <p className="max-w-xs text-xs text-white/70">
                Allow camera access when prompted so the image scanner can match hunt targets.
              </p>
            </>
          ) : (
            <>
              <AlertCircleIcon className="h-10 w-10 text-amber-300" />
              <p className="text-sm font-medium text-white">
                {cameraState === 'denied' ? 'Camera permission denied' : 'Camera unavailable'}
              </p>
              <p className="max-w-xs text-xs text-white/70">
                {cameraError ?? 'You can still scan a photo of the target image below.'}
              </p>
              <Button variant="secondary" size="sm" onClick={onRetryCamera}>
                Try camera again
              </Button>
            </>
          )}
        </div>
      )}

      <FireworksStreamersOverlay trigger={celebrationTrigger} />

      {/* Top bar */}
      <div className="absolute left-0 right-0 top-0 z-20 flex items-center gap-2 bg-black/50 p-3 backdrop-blur-sm">
        <Button variant="ghost" size="icon" className="text-white" onClick={onBack}>
          <ArrowLeftIcon className="h-5 w-5" />
        </Button>
        <div className="flex items-center gap-2 text-white">
          <ScanLineIcon className="h-4 w-4" />
          <span className="text-sm font-medium">CV Target Scanner</span>
        </div>
        <Button
          variant="ghost"
          size="sm"
          className="ml-auto text-white hover:bg-white/20"
          onClick={() => setShowTargets((s) => !s)}
        >
          <TargetIcon className="mr-1 h-4 w-4" />
          {targets.length} Targets
        </Button>
      </div>

      {/* Scan animation — moving horizontal line when no match */}
      {cameraReady && !bestMatch && (
        <div className="absolute inset-0 z-10 pointer-events-none">
          <div
            className="absolute left-0 right-0 h-0.5 bg-cyan-400/80 shadow-[0_0_12px_2px_rgba(34,211,238,0.6)]"
            style={{ top: `${scanProgress * 100}%` }}
          />
          {/* Corner brackets */}
          {/* Full-screen targeting reticle */}
          <div className="absolute inset-4 border-2 border-cyan-400/25 rounded-2xl" />
          {/* Corner accents */}
          <div className="absolute top-4 left-4 h-8 w-8 border-t-2 border-l-2 border-cyan-400/60 rounded-tl-lg" />
          <div className="absolute top-4 right-4 h-8 w-8 border-t-2 border-r-2 border-cyan-400/60 rounded-tr-lg" />
          <div className="absolute bottom-4 left-4 h-8 w-8 border-b-2 border-l-2 border-cyan-400/60 rounded-bl-lg" />
          <div className="absolute bottom-4 right-4 h-8 w-8 border-b-2 border-r-2 border-cyan-400/60 rounded-br-lg" />
        </div>
      )}

      {/* Bounding box + confidence badge when matched */}
      {bestMatch && boundingBox && (
        <div className="absolute inset-0 z-10 pointer-events-none">
          <div
            className={cn(
              'absolute border-2 rounded-lg transition-all duration-300',
              justMatched
                ? 'border-green-400 shadow-[0_0_20px_4px_rgba(74,222,128,0.4)]'
                : 'border-green-400/70'
            )}
            style={{
              left: `${boundingBox.x * 100}%`,
              top: `${boundingBox.y * 100}%`,
              width: `${boundingBox.w * 100}%`,
              height: `${boundingBox.h * 100}%`,
            }}
          >
            {/* Corner accents */}
            <div className="absolute -top-1 -left-1 h-4 w-4 border-t-2 border-l-2 border-green-400" />
            <div className="absolute -top-1 -right-1 h-4 w-4 border-t-2 border-r-2 border-green-400" />
            <div className="absolute -bottom-1 -left-1 h-4 w-4 border-b-2 border-l-2 border-green-400" />
            <div className="absolute -bottom-1 -right-1 h-4 w-4 border-b-2 border-r-2 border-green-400" />

            {/* Target name label */}
            <div className="absolute -top-9 left-0 right-0 flex justify-center">
              <div className="rounded-full bg-green-500 px-3 py-1 text-xs font-bold text-black shadow-lg">
                {bestMatch.targetName}
              </div>
            </div>

            {/* Confidence score */}
            <div className="absolute -bottom-8 left-1/2 -translate-x-1/2">
              <div className="flex items-center gap-1 rounded-full bg-black/70 px-2.5 py-1 backdrop-blur-sm">
                <CheckCircle2Icon className="h-3 w-3 text-green-400" />
                <span className="text-xs font-semibold text-white">
                  {Math.round(bestMatch.score * 100)}% match
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Show celebration only after the API has verified and recorded the capture. */}
      {/* Full-screen celebration overlay — Record button on match, Continue on verification */}
      {(pendingMatch || verifiedCapture) && (
        <div className="absolute inset-0 z-30 flex flex-col items-center justify-center bg-black/70 p-6">
          {pendingMatch && !verifiedCapture && (
            <div className="relative z-10 flex flex-col items-center gap-4 text-center">
              <div className="flex h-20 w-20 items-center justify-center rounded-full bg-green-500/20 ring-4 ring-green-500/30">
                <TargetIcon className="h-10 w-10 text-green-400" />
              </div>
              <h2 className="text-2xl font-bold text-white">Target Found!</h2>
              <p className="text-lg font-medium text-green-300">{pendingMatch.targetName}</p>
              <p className="text-sm text-white/70">{Math.round(pendingMatch.score * 100)}% confidence match</p>
              <Button size="lg" className="mt-4 min-w-40" onClick={onRecord} disabled={isRecording}>
                {isRecording ? (
                  <>
                    <Loader2Icon className="mr-2 h-5 w-5 animate-spin" />
                    Recording…
                  </>
                ) : (
                  <>
                    <CheckCircle2Icon className="mr-2 h-5 w-5" />
                    Record
                  </>
                )}
              </Button>
              <Button variant="ghost" size="sm" className="text-white/60" onClick={onSkipMatch}>
                Skip
              </Button>
            </div>
          )}
          {verifiedCapture && !pendingMatch && (
            <div className="relative z-10 flex flex-col items-center gap-4 text-center">
              <div className="flex h-20 w-20 items-center justify-center rounded-full bg-green-500/20 ring-4 ring-green-500/30">
                <TrophyIcon className="h-10 w-10 text-green-400" />
              </div>
              <h2 className="text-2xl font-bold text-white">Challenge Complete!</h2>
              <p className="text-lg font-medium text-green-300">+{verifiedCapture.pointsAwarded} points</p>
              <Button size="lg" className="mt-4 min-w-40" onClick={onDismissVerified}>
                <ScanLineIcon className="mr-2 h-5 w-5" />
                Continue Scanning
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Bottom info bar */}
      <div className="absolute bottom-0 left-0 right-0 z-20 bg-black/50 p-4 pb-[calc(1rem+env(safe-area-inset-bottom))] backdrop-blur-sm">
        <label className="mb-3 flex cursor-pointer items-center justify-center gap-2 rounded-md border border-white/30 bg-white/10 px-3 py-2 text-sm font-medium text-white transition hover:bg-white/20">
          <UploadIcon className="h-4 w-4" />
          Scan a photo instead
          <input
            className="sr-only"
            type="file"
            accept="image/jpeg,image/png,image/webp"
            onChange={(event) => {
              const file = event.target.files?.[0];
              if (file) onPhotoSelected(file);
              event.currentTarget.value = '';
            }}
          />
        </label>
        {captureError && <p className="mb-2 text-center text-xs font-medium text-amber-200">{captureError}</p>}
        {targets.length === 0 && (
          <div className="flex flex-col items-center gap-2 text-center">
            <TargetIcon className="h-6 w-6 text-white/60" />
            <p className="text-sm font-medium text-white">No CV targets registered</p>
            <p className="max-w-xs text-xs text-white/70">
              An admin needs to upload target images in the CV Training Studio before scanning will work.
            </p>
          </div>
        )}

        {targets.length > 0 && cameraReady && !bestMatch && (
          <div className="flex items-center justify-center gap-2 text-white">
            <ScanLineIcon className="h-4 w-4 animate-pulse text-cyan-400" />
            <span className="text-sm">Scanning for targets…</span>
          </div>
        )}

        {targets.length > 0 && bestMatch && (
          <div className="flex items-center justify-center gap-2 text-white">
            <CheckCircle2Icon className="h-4 w-4 text-green-400" />
            <span className="text-sm">
              Detected: {bestMatch.targetName} · {Math.round(bestMatch.score * 100)}% confidence
            </span>
          </div>
        )}

        {/* Top 3 candidate scores */}
        {allResults.length > 0 && (
          <div className="mt-2 flex justify-center gap-2">
            {allResults.slice(0, 3).map((r) => (
              <div
                key={r.targetId}
                className={cn(
                  'flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium',
                  r.matched
                    ? 'bg-green-500/30 text-green-300'
                    : 'bg-white/10 text-white/60'
                )}
              >
                {r.targetName.length > 12 ? r.targetName.slice(0, 10) + '…' : r.targetName}
                <span className="tabular-nums">{Math.round(r.score * 100)}%</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Targets drawer */}
      {showTargets && (
        <div
          className="fixed inset-0 z-30 bg-black/50"
          onClick={() => setShowTargets(false)}
        >
          <div
            className="absolute bottom-0 left-0 right-0 max-h-[60vh] overflow-y-auto rounded-t-2xl bg-background p-4 pb-[calc(1rem+env(safe-area-inset-bottom))]"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-3 flex items-center justify-between">
              <h3 className="font-semibold">Active CV Targets</h3>
              <Button variant="ghost" size="icon" onClick={() => setShowTargets(false)}>
                <XIcon className="h-5 w-5" />
              </Button>
            </div>

            {targets.length === 0 ? (
              <p className="py-6 text-center text-sm text-muted-foreground">
                No targets registered yet.
              </p>
            ) : (
              <div className="space-y-2">
                {targets.map((t) => {
                  const result = allResults.find((r) => r.targetId === t.id);
                  const score = result?.score ?? 0;
                  const matched = result?.matched ?? false;
                  return (
                    <div
                      key={t.id}
                      className={cn(
                        'flex items-center gap-3 rounded-lg border p-3',
                        matched ? 'border-green-400 bg-green-50 dark:bg-green-950/30' : 'border-border'
                      )}
                    >
                      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-muted">
                        <TargetIcon className="h-5 w-5 text-muted-foreground" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="truncate text-sm font-medium">{t.name}</p>
                        <p className="text-xs text-muted-foreground">
                          Threshold: {Math.round(t.confidenceThreshold * 100)}%
                        </p>
                      </div>
                      <div className="text-right">
                        <p
                          className={cn(
                            'text-sm font-bold tabular-nums',
                            matched ? 'text-green-600 dark:text-green-400' : 'text-muted-foreground'
                          )}
                        >
                          {Math.round(score * 100)}%
                        </p>
                        {matched && (
                          <Badge variant="default" className="bg-green-600 text-[10px]">
                            MATCH
                          </Badge>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {cvScanChallenge && (
              <div className="mt-4 rounded-lg border border-cyan-300 bg-cyan-50 p-3 dark:bg-cyan-950/30">
                <div className="flex items-center gap-2">
                  <TrophyIcon className="h-4 w-4 text-cyan-600 dark:text-cyan-400" />
                  <p className="text-sm font-medium">
                    Active challenge: {cvScanChallenge.title}
                  </p>
                </div>
                <p className="mt-1 text-xs text-muted-foreground">
                  Scan a registered target to auto-complete this challenge (+{cvScanChallenge.points} pts)
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
